/* RESPONSIBLE TEAM: team-inbox-experience */
// @ts-nocheck
import { inject as service } from '@ember/service';
import { setOwner } from '@ember/application';
import {
  type ComposerConfig,
  EMOJI_TYPEAHEAD,
  type InitialSelection,
  INPUT_RULE_CODE_BLOCK,
  INPUT_RULE_EMOJI,
  INPUT_RULE_INLINE_CODE,
  INPUT_RULE_MARKDOWN_ANCHOR,
  INPUT_RULE_ORDERED_LIST,
  INPUT_RULE_UNORDERED_LIST,
  type TextDirection,
  type TypeaheadConfig,
} from '@intercom/composer-ember';
import type Session from 'embercom/services/session';
import { triggerCharacter } from '@intercom/composer-ember/lib/composer/typeahead/trigger-character';
import EmbercomFileUploader from '../articles/embercom-file-uploader';
import { type CanvasNetworkClient } from '@intercom/interblocks.ts';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { EmbercomCanvasNetworkClient } from 'embercom/objects/composer/embercom-canvas-network-client';

export const MENTION_TYPEAHEAD = {
  name: 'mention',
  matcher: triggerCharacter('@', { allowSpaces: true }),

  component: 'conversation/composer/mention-typeahead',
} as TypeaheadConfig;

function channelMentionTypeahead(channelType: 'slack' | 'discord'): TypeaheadConfig {
  return {
    name: `${channelType}_mention`,
    matcher: triggerCharacter('@', { allowSpaces: true }),
    component: 'inbox2/composer/mention-typeahead',
    attrs: { mentionType: channelType },
  } as TypeaheadConfig;
}

export const SAVED_REPLY_TYPEAHEAD = {
  name: 'saved_reply',
  matcher: triggerCharacter('#', { allowSpaces: true }),
  component: 'conversation/composer/saved-reply-typeahead',
  attrs: {},
} as TypeaheadConfig;

export const CUSTOM_EMOJI_TYPEAHEAD = {
  name: 'custom_emoji',
  matcher: triggerCharacter(':', { minLength: 2 }),
  component: 'tools/typeaheads/custom-emoji',
} as TypeaheadConfig;

type Pane = 'note' | 'reply';

export default class InboxComposerConfig implements ComposerConfig {
  @service declare session: Session;

  upload;
  attributes;
  templating;

  debug = false;
  renderOverlaysInPlace = false;
  autoFocus = false;

  tools = [
    { name: 'text-formatter' },
    { name: 'anchor-editor' },
    { name: 'fallback-editor' },
    { name: 'image-editor', options: { supportAltAttributeEditing: false } },
  ];

  typeaheads = []; // Will be set in constructor

  textDirection = 'auto' as TextDirection;
  placeholder = '';
  initialSelectionLocation = 'end' as InitialSelection;
  canvasNetworkClient: CanvasNetworkClient;

  allowedBlocks = [
    'paragraph',
    'image',
    'orderedList',
    'unorderedList',
    'heading',
    'subheading',
    'codeBlock',
  ];

  allowedInline = ['bold', 'italic', 'anchor', 'code'];

  inputRules = [
    INPUT_RULE_CODE_BLOCK,
    INPUT_RULE_INLINE_CODE,
    INPUT_RULE_ORDERED_LIST,
    INPUT_RULE_UNORDERED_LIST,
    INPUT_RULE_EMOJI,
    INPUT_RULE_MARKDOWN_ANCHOR,
  ];

  allowTextAlignment = false;

  constructor(
    owner: unknown,
    app: any,
    conversation: any,
    activePane: Pane,
    allowUploads: boolean,
    placeholder: string,
    macro: any,
    enableTemplating: boolean,
  ) {
    setOwner(this, owner);

    // Safely check for custom emojis feature - fallback to standard emoji if session/workspace not available
    let hasCustomEmojisFeature = false;
    try {
      hasCustomEmojisFeature =
        this.session?.workspace?.isFeatureEnabled?.('team-messenger-custom-emojis') ?? false;
    } catch (error) {
      // Workspace not initialized yet, fall back to standard emoji
      hasCustomEmojisFeature = false;
    }

    if (hasCustomEmojisFeature) {
      this.typeaheads = [CUSTOM_EMOJI_TYPEAHEAD];
    } else {
      this.typeaheads = [EMOJI_TYPEAHEAD];
    }

    this.configureUploads(app.id, allowUploads);
    this.configureTypeaheads(app, activePane, conversation, macro);
    this.configureSchema(app, activePane, conversation);
    this.canvasNetworkClient = new EmbercomCanvasNetworkClient(app.id);
    this.placeholder = placeholder;
    this.attributes = app.allowedAttributes;

    if (enableTemplating) {
      this.templating = {
        picker: 'common/attribute-picker',
        resolver: new AttributeInfoResolver({ attributes: this.attributes }),
      };
    }
  }

  private configureUploads(appId: string, allowUploads: boolean) {
    if (allowUploads) {
      this.upload = {
        allowedImageFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
        allowedAttachmentTypes: ['*'],
        uploader: EmbercomFileUploader,
        attrs: { policyUrl: `/apps/${appId}/uploads` },
      };
    }
  }

  private configureTypeaheads(app: any, activePane: Pane, conversation: any, macro: any) {
    let channelType = this.getChannelType(conversation);

    if (activePane === 'note') {
      this.typeaheads.push(MENTION_TYPEAHEAD);
      this.typeaheads.push(
        Object.assign({}, SAVED_REPLY_TYPEAHEAD, {
          attrs: { conversation, macro, activePane, bulkInsert: false },
        }),
      );
    } else {
      if (channelType) {
        this.typeaheads.push(
          Object.assign({}, channelMentionTypeahead(channelType), {
            attrs: { conversation, mentionType: channelType },
          }),
        );
      }
      this.typeaheads.push(
        Object.assign({}, SAVED_REPLY_TYPEAHEAD, {
          attrs: { conversation, macro, activePane, bulkInsert: false },
        }),
      );
    }
  }

  private getChannelType(conversation: any): 'slack' | 'discord' | undefined {
    if (!conversation || !conversation.attributes) {
      return undefined;
    }

    let hasSlackWorkspace = conversation.attributes.some(
      (attr: any) => attr.descriptor?.id === 'slack_workspace',
    );
    let hasSlackChannel = conversation.attributes.some(
      (attr: any) =>
        attr.descriptor?.id === 'slack_channel' || attr.descriptor?.id === 'slack_dm_channel',
    );
    if (hasSlackWorkspace && hasSlackChannel) {
      return 'slack';
    }

    let hasDiscordChannel = conversation.attributes.some(
      (attr: any) => attr.descriptor?.id === 'discord_channel',
    );
    if (hasDiscordChannel) {
      return 'discord';
    }

    return undefined;
  }

  private configureSchema(app: any, activePane: Pane, conversation: any) {
    if (activePane === 'note') {
      this.allowedInline.push('mention');
      this.allowedBlocks.push('messengerCard');
    } else {
      if (this.getChannelType(conversation)) {
        this.allowedInline.push('mention');
      }
      this.allowedBlocks.push('messengerCard');
    }
  }
}
